/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { marked } from "marked";
import { AuroraBackground } from "@/components/AuroraBackground";
import { Reveal } from "@/components/Reveal";
import { StoreBadgeGroup } from "@/components/StoreBadges";
import { Calendar, Sparkles } from "lucide-react";

// Configure marked once (module scope).
marked.setOptions({ gfm: true, breaks: false });

const sanitizeHtml = (unsafeHtml) => {
  if (!unsafeHtml || typeof window === "undefined") return unsafeHtml || "";

  const template = window.document.createElement("template");
  template.innerHTML = unsafeHtml;

  // Remove executable/embedding elements entirely.
  template.content
    .querySelectorAll("script, style, iframe, object, embed, link, meta, base")
    .forEach((node) => node.remove());

  template.content.querySelectorAll("*").forEach((el) => {
    [...el.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = (attr.value || "").trim();

      if (name.startsWith("on")) {
        el.removeAttribute(attr.name);
        return;
      }

      if (
        (name === "href" || name === "src" || name === "xlink:href") &&
        /^(javascript:|data:text\/html)/i.test(value)
      ) {
        el.removeAttribute(attr.name);
      }
    });

    if (el.tagName === "A") {
      const href = (el.getAttribute("href") || "").trim();
      if (/^https?:\/\//i.test(href)) {
        el.setAttribute("rel", "noopener noreferrer");
        el.setAttribute("target", "_blank");
      }
    }
  });

  return template.innerHTML;
};

/**
 * Parse the CHANGELOG.md into a list of entries:
 *   [{ version, date, title, htmlBody }]
 *
 * Headings of the form `## 2026.06.10 — Title` start a new entry.
 * The intro paragraphs before the first H2 become the page lead.
 */
const parseChangelog = (md) => {
  const lines = md.split("\n");
  const entries = [];
  let lead = [];
  let current = null;

  const headingRe = /^##\s+(?:\[)?([0-9]{4}[.\-/][0-9]{1,2}[.\-/][0-9]{1,2})(?:\])?\s*[—\-–:]\s*(.+)$/;

  for (const line of lines) {
    const m = line.match(headingRe);
    if (m) {
      if (current) entries.push(current);
      current = { version: m[1].replace(/-/g, "."), title: m[2].trim(), body: [] };
      continue;
    }
    if (!current) {
      lead.push(line);
    } else {
      current.body.push(line);
    }
  }
  if (current) entries.push(current);

  const intro = lead.join("\n").replace(/^#\s+.+$/m, "").trim();

  return {
    intro: intro ? sanitizeHtml(marked.parse(intro)) : "",
    entries: entries.map((e) => ({
      ...e,
      htmlBody: sanitizeHtml(marked.parse(e.body.join("\n").trim())),
    })),
  };
};

const ChangelogPage = () => {
  const { t } = useTranslation("site");
  const [{ intro, entries }, setParsed] = useState({ intro: "", entries: [] });
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch("/CHANGELOG.md", { headers: { Accept: "text/markdown,text/plain" } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((md) => {
        if (!cancelled) setParsed(parseChangelog(md));
      })
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const formattedDate = (raw) => {
    try {
      const norm = raw.replace(/\./g, "-");
      const d = new Date(norm);
      if (Number.isNaN(d.getTime())) return raw;
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (_) {
      return raw;
    }
  };

  const cardBg = useMemo(
    () => [
      "var(--aurora-coach)",
      "var(--aurora-journeys)",
      "var(--aurora-emotional)",
      "var(--aurora-analytics)",
      "var(--aurora-quests)",
    ],
    []
  );

  return (
    <>
      <section className="relative pb-10">
        <AuroraBackground variant="soft" />
        <div className="relative mx-auto max-w-4xl px-5 lg:px-8 pt-8 lg:pt-12">
          <Reveal>
            <p className="eyebrow">{t("changelog.kicker", "Changelog")}</p>
            <h1 className="mt-3 font-serif text-[2.4rem] sm:text-5xl lg:text-[4rem] tracking-tight font-light leading-[1.05]">
              {t("changelog.title1", "Built in public.")}{" "}
              <em className="italic aurora-shimmer-text">
                {t("changelog.title2", "Shipped daily.")}
              </em>
            </h1>
            <p className="mt-5 max-w-2xl text-white/72 text-lg font-light leading-relaxed">
              {t(
                "changelog.subtitle",
                "Every meaningful change we ship to withAurora.love — from new features to quiet fixes — lives here. No marketing fluff. Just the work."
              )}
            </p>
          </Reveal>
        </div>
      </section>

      {error && (
        <section className="relative pb-20">
          <div className="mx-auto max-w-3xl px-5 lg:px-8 text-white/70 font-light">
            We couldn't load the changelog right now. Please refresh in a moment.
          </div>
        </section>
      )}

      {intro && !error && (
        <section className="relative pb-4">
          <div
            className="mx-auto max-w-3xl px-5 lg:px-8 text-white/65 font-light leading-relaxed prose-aurora"
            dangerouslySetInnerHTML={{ __html: intro }}
          />
        </section>
      )}

      <section className="relative py-10 lg:py-14">
        <div className="mx-auto max-w-4xl px-5 lg:px-8 space-y-6" data-testid="changelog-entries">
          {entries.map((e, i) => (
            <Reveal key={`${e.version}-${i}`} delay={(i % 5) * 50}>
              <article
                className="aurora-card p-6 lg:p-9"
                style={{ background: cardBg[i % cardBg.length] }}
                data-testid={`changelog-entry-${e.version}`}
              >
                <div className="flex items-center gap-3 mb-4 text-[0.7rem] tracking-[0.22em] uppercase">
                  <span className="inline-flex items-center gap-1.5 text-[var(--aurora-gold)]">
                    <Calendar size={12} />
                    <time dateTime={e.version}>{formattedDate(e.version)}</time>
                  </span>
                  <span className="text-white/30">·</span>
                  <span className="text-white/50 font-mono normal-case tracking-normal">{e.version}</span>
                </div>
                <h2 className="font-serif text-2xl sm:text-3xl font-normal leading-snug flex items-start gap-3">
                  <Sparkles size={18} className="mt-2 text-[var(--aurora-gold)] shrink-0" />
                  <span>{e.title}</span>
                </h2>
                <div
                  className="prose-aurora mt-5 text-white/78 font-light leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: e.htmlBody }}
                />
              </article>
            </Reveal>
          ))}
          {!error && entries.length === 0 && (
            <p className="text-white/55 font-light italic">Loading the latest releases…</p>
          )}
        </div>
      </section>

      <section className="relative py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight font-light">
              {t("changelog.closing1", "What we ship next")}{" "}
              <em className="italic text-[var(--aurora-gold)]">{t("changelog.closing2", "starts inside the app.")}</em>
            </h2>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <StoreBadgeGroup startLabel={t("common.startFree")} />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default ChangelogPage;
